import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  getAnnualBalanceSheetByTokenQuery,
  getAnnualEvaluationByIdQuery,
  saveAnnualEvaluationQuery,
  sendAnnualEvaluationReminderQuery,
  exportAnnualBalanceSheetPdfQuery,
  downloadAnnualEvaluationFileQuery,
  uploadAnnualEvaluationFieldFileQuery,
} from "./queries";
import {
  ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_ACTION,
  QUERY_STATUS,
} from "constants/constants";
import { downloadFile } from "utils/helper";

const AnnualBalancesheetContext = createContext({});

export const useAnnualBalancesheetContext = () =>
  useContext(AnnualBalancesheetContext);

export const AnnualBalancesheetProvider = ({ ...props }) => {
  const [scores, setScores] = useState({});
  const [missionAverages, setMissionAverages] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const reminderRequestsRef = useRef(new Set());
  const [annualBalancesheetByToken, setAnnualBalancesheetByToken] = useState(
    {}
  );
  const [savedEvaluation, setSavedEvaluation] = useState({});
  const [saveEvaluationStatus, setSaveEvaluationStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [getEvaluationStatus, setGetEvaluationStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [exportPdfStatus, setExportPdfStatus] = useState(QUERY_STATUS.INITIAL);
  const updateMissionScores = (missionId, newScores) => {
    setScores((prevScores) => ({
      ...prevScores,
      [missionId]: newScores,
    }));
  };
  const updateMissionAverage = (missionId, newAverage) => {
    setMissionAverages((prevAverages) => ({
      ...prevAverages,
      [missionId]: newAverage,
    }));
  };
  const updateTotalScore = (newTotalScore) => {
    setTotalScore(newTotalScore);
  };
  const resetAnnualBalancesheetComputedState = useCallback(() => {
    setScores({});
    setMissionAverages({});
    setTotalScore(0);
  }, []);

  const getAnnualBalanceSheetByToken = useCallback(
    async ({ slug, token, page }) => {
      setGetEvaluationStatus(QUERY_STATUS.LOADING);
      try {
        const result = await getAnnualBalanceSheetByTokenQuery({
          slug,
          token,
          page,
        });
        setAnnualBalancesheetByToken(result);
        setGetEvaluationStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setGetEvaluationStatus(QUERY_STATUS.ERROR);

        console.error("getAnnualBalanceSheetByToken", e);
        throw e;
      }
    },
    []
  );
  const saveAnnualEvaluation = useCallback(async (data) => {
    setSaveEvaluationStatus(QUERY_STATUS.LOADING);
    setSavedEvaluation({});
    try {
      const result = await saveAnnualEvaluationQuery({
        id: data.id,
        data: data,
      });
      setSavedEvaluation(result);
      setSaveEvaluationStatus(QUERY_STATUS.SUCCESS);
    } catch (e) {
      const badRequest = e?.response?.data?.["bad-request"];
      const hasFormFeedback =
        badRequest &&
        typeof badRequest === "object" &&
        (Array.isArray(badRequest?.errors) ||
          badRequest?.warning ||
          badRequest?.action !== undefined);

      if (hasFormFeedback) {
        setSavedEvaluation(badRequest);
        setSaveEvaluationStatus(QUERY_STATUS.SUCCESS);
        return;
      }

      // Manager publish: the salary line could not be created, the evaluation stays editable
      const internalError = e?.response?.data?.["internal-server-error"];
      if (
        internalError?.action === ANNUAL_BALANCESHEET_SALARY_NOT_CREATED_ACTION
      ) {
        setSavedEvaluation(internalError);
        setSaveEvaluationStatus(QUERY_STATUS.SUCCESS);
        return;
      }

      setSaveEvaluationStatus(QUERY_STATUS.ERROR);

      console.error("saveAnnualEvaluation", e);
    }
  }, []);

  const sendAnnualEvaluationReminder = useCallback(
    async ({ person_id, evaluation_id }) => {
      const reminderKey = `${person_id}-${evaluation_id}`;
      if (reminderRequestsRef.current.has(reminderKey)) return null;

      reminderRequestsRef.current.add(reminderKey);
      try {
        const result = await sendAnnualEvaluationReminderQuery({
          person_id,
          evaluation_id,
        });
        setAnnualBalancesheetByToken(result);
        return result;
      } catch (e) {
        console.error("sendAnnualEvaluationReminder", e);
        throw e;
      } finally {
        reminderRequestsRef.current.delete(reminderKey);
      }
    },
    []
  );

  const getAnnualEvaluationById = useCallback(
    async ({ person_id, evaluation_id, isExport }) => {
      try {
        const result = await getAnnualEvaluationByIdQuery({
          person_id,
          evaluation_id,
          isExport,
        });
        setAnnualBalancesheetByToken(result);
      } catch (e) {
        console.error("getAnnualEvaluationById", e);
      }
    },
    []
  );

  const annualBalanceSheetPdfDownloadQuery = useCallback(
    async ({ userScope, queryParams }) =>
      exportAnnualBalanceSheetPdfQuery({
        scope: userScope,
        person_id: queryParams?.person_id,
        evaluation_id: queryParams?.evaluation_id,
      }),
    []
  );

  const exportAnnualBalanceSheetPdf = useCallback(
    async ({ scope, person_id, evaluation_id }) => {
      setExportPdfStatus(QUERY_STATUS.LOADING);
      try {
        await downloadFile({
          fileType: "application/pdf",
          fileName: `bilan-annuel-${person_id}.pdf`,
          downloadFileQuery: annualBalanceSheetPdfDownloadQuery,
          userScope: scope,
          queryParams: {
            person_id,
            evaluation_id,
          },
        });
        setExportPdfStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setExportPdfStatus(QUERY_STATUS.ERROR);
        console.error("exportAnnualBalanceSheetPdf", e);
      }
    },
    [annualBalanceSheetPdfDownloadQuery]
  );

  const uploadAnnualEvaluationFieldFile = useCallback(async (data) => {
    try {
      return await uploadAnnualEvaluationFieldFileQuery(data);
    } catch (e) {
      console.error("uploadAnnualEvaluationFieldFile", e);
      throw e;
    }
  }, []);

  const annualEvaluationFileDownloadQuery = useCallback(
    async ({ file_id }) => await downloadAnnualEvaluationFileQuery({ file_id }),
    []
  );

  const downloadAnnualEvaluationFile = useCallback(
    async ({ file_id, file_name }) => {
      try {
        await downloadFile({
          fileType: "application/octet-stream",
          fileName: file_name,
          file_id,
          downloadFileQuery: annualEvaluationFileDownloadQuery,
        });
      } catch (e) {
        console.error("downloadAnnualEvaluationFile", e);
        throw e;
      }
    },
    [annualEvaluationFileDownloadQuery]
  );

  const values = useMemo(
    () => ({
      getAnnualBalanceSheetByToken,
      annualBalancesheetByToken,
      saveAnnualEvaluation,
      setSavedEvaluation,
      savedEvaluation,
      scores,
      setScores,
      missionAverages,
      setMissionAverages,
      updateMissionScores,
      updateMissionAverage,
      updateTotalScore,
      resetAnnualBalancesheetComputedState,
      totalScore,
      setTotalScore,
      getAnnualEvaluationById,
      sendAnnualEvaluationReminder,
      saveEvaluationStatus,
      getEvaluationStatus,
      exportAnnualBalanceSheetPdf,
      downloadAnnualEvaluationFile,
      exportPdfStatus,
      setExportPdfStatus,
      uploadAnnualEvaluationFieldFile,
    }),
    [
      setAnnualBalancesheetByToken,
      annualBalancesheetByToken,
      setSavedEvaluation,
      savedEvaluation,
      scores,
      setScores,
      missionAverages,
      setMissionAverages,
      updateMissionScores,
      updateMissionAverage,
      updateTotalScore,
      resetAnnualBalancesheetComputedState,
      totalScore,
      setTotalScore,
      getAnnualEvaluationById,
      sendAnnualEvaluationReminder,
      saveEvaluationStatus,
      getEvaluationStatus,
      exportAnnualBalanceSheetPdf,
      downloadAnnualEvaluationFile,
      exportPdfStatus,
      uploadAnnualEvaluationFieldFile,
    ]
  );

  return <AnnualBalancesheetContext.Provider value={values} {...props} />;
};
